import { FeatureKey } from 'common/constants/permission'
import PermissionGuard from 'common/guards/PermissionGuard'
import { fNumber } from 'common/utils/formatNumber'
import {
  useGetBreedName,
  useGetPondDashboard,
  useGetPondDashboardFinance,
  useGetPondInfo,
} from 'modules/pond/api/queries'
import {
  useGetSpeciesSizeUnit,
  useIsFishSpecie,
  useSpeciesId,
  useSpeciesName,
} from 'modules/pond/api/selector'
import { PondStatus } from 'modules/pond/constants'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import { Card, Stack, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'

import DashboardChart from '../DashboardChart'
import IncomeChart from '../IncomeChart'
import ProductionChart from '../ProductionChart'

type InfoKey =
  | 'production'
  | 'accumulatedDeadShrimpCount'
  | 'initialShrimpCount'
  | 'shrimpCountPerKg'
  | 'fcr'
  | 'accumulatedFood'
  | 'adg'
  | 'survivalRate'
  | 'pondSize'
  | 'pondType'
  | 'saltiness'
  | 'density'

interface InfoItem {
  title: React.ReactNode
  value: any | undefined
  unit?: string
  isFull?: boolean
  change?: string
  info?: React.ReactNode
}

const OverviewDashboard = () => {
  const { t } = useTranslation(['pond', 'pondComparison'])
  const theme = useTheme()
  const { data, isFetching } = useGetPondDashboard()
  const { data: financeDataRes } = useGetPondDashboardFinance()
  const speciesName = useSpeciesName()
  const speciesId = useSpeciesId()
  const speciesSizeUnit = useGetSpeciesSizeUnit(speciesId)
  const breedName = useGetBreedName(data?.breedId)
  const isFishSpecie = useIsFishSpecie()

  const { data: pondDashboard, isFetching: isPondDashboardFetching } = useGetPondInfo()

  const showGrowthRecordWarning =
    data?.status !== PondStatus.Completed && data?.support.hasGrowthRecord === false
  // DOF-1357: FCR / survival rate depend on the estimated yield
  const noFoodData = data?.support.yieldEstimatable === false

  const financeData = {
    profit: financeDataRes?.profit ?? 0,
    profitPercentage: financeDataRes?.profitPercentage ?? 0,
    cost: financeDataRes?.cost ?? 0,
    revenue: {
      total: financeDataRes?.revenue?.total ?? 0,
      sold: financeDataRes?.revenue?.sold ?? 0,
      inPond: financeDataRes?.revenue?.inPond ?? 0,
    },
  }

  const info: Record<InfoKey, InfoItem> = {
    production: {
      title: t('total.production'),
      value: data?.shrimpWeight,
      unit: t('kg.thai.unit'),
    },
    accumulatedDeadShrimpCount: {
      title: <Trans i18nKey={'pond:acc.animal.dead'} values={{ species: speciesName }} />,
      value: fNumber(data?.accumulatedDeadShrimp ?? 0),
      unit: isFishSpecie ? t('pond:shrimp.unit') : t('kg.thai.unit'),
      info: t('pond:acc.animal.dead.tooltip'),
    },
    initialShrimpCount: {
      title: <Trans i18nKey={'pond:animal.amount.release'} values={{ species: speciesName }} />,
      value: fNumber(data?.initialShrimpCount ?? 0),
      info: t('pond:animal.amount.release.tooltip'),
    },
    shrimpCountPerKg: {
      title: <Trans i18nKey={'pond:animal.size'} values={{ species: speciesName }} />,
      value: showGrowthRecordWarning ? '-' : fNumber(data?.shrimpCountPerKg ?? 0),
      unit: showGrowthRecordWarning ? '' : speciesSizeUnit,
      info: t('pond:animal.size.tooltip'),
    },
    fcr: {
      title: t('fcr'),
      value: showGrowthRecordWarning || noFoodData ? '-' : fNumber(data?.FCR ?? 0),
      info: t('pond:fcr.tooltip'),
    },
    adg: {
      title: t('adg'),
      value: showGrowthRecordWarning ? '-' : fNumber(data?.ADG ?? 0),
      info: t('pond:adg.tooltip'),
    },
    accumulatedFood: {
      title: t('acc.food'),
      value: fNumber(data?.accumulatedFood ?? 0),
      unit: t('kg.thai.unit'),
      info: t('pond:acc.food.tooltip'),
    },
    survivalRate: {
      title: t('pond:survive.rate'),
      value: noFoodData ? '-' : fNumber(data?.survivalRate ?? 0),
      unit: noFoodData ? '' : '%',
      info: t('pond:survive.rate.tooltip'),
    },
    pondSize: {
      title: t('pond:pond.size'),
      value: fNumber(pondDashboard?.pondSize ?? 0),
      unit: pondDashboard?.areaUnit,
    },
    pondType: {
      title: t('pond:pond.type'),
      value: pondDashboard?.pondType,
    },
    saltiness: {
      title: t('pond:saltiness'),
      value: fNumber(pondDashboard?.salinity ?? 0),
      unit: 'ppt',
    },
    density: {
      title: t('pond:density'),
      value: pondDashboard?.pondSize
        ? fNumber((data?.initialShrimpCount || 0) / pondDashboard.pondSize)
        : fNumber(0),
      unit: pondDashboard?.areaUnit
        ? `${t('pond:shrimp.unit')}/${pondDashboard.areaUnit}`
        : t('pond:shrimp.per.unit'),
      isFull: true,
      info: t('pond:density.tooltip'),
    },
  }

  const dashboardInfo = [
    'fcr',
    'adg',
    'accumulatedDeadShrimpCount',
    'accumulatedFood',
    'survivalRate',
    'shrimpCountPerKg',
    'initialShrimpCount',
    'pondSize',
    'pondType',
    'saltiness',
    'density',
  ] as InfoKey[]

  return (
    <>
      <Grid container spacing={2}>
        <Card sx={{ width: '100%', p: 3, mt: 3, ml: 2 }}>
          <Typography color="text.primary" fontSize={18} fontWeight={600} sx={{ mb: 2 }}>
            {t('pond:overview.title')}
          </Typography>

          {isFetching ? (
            <Skeleton
              variant="text"
              width={200}
              height={20}
              sx={{ bgcolor: 'grey.300', borderRadius: 1, mb: 3 }}
            />
          ) : data && pondDashboard && data.issues && data.issues.length > 0 ? (
            <Grid
              item
              xs={12}
              lg={12}
              sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, mb: 3 }}
            >
              <Card
                sx={{
                  width: '100%',
                  height: '100%',
                  p: 2,
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: theme.palette.grey[300],
                }}
              >
                <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      p: 1.5,
                      borderRadius: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 21 }} />
                  </Box>

                  <Stack sx={{ gap: 1 }}>
                    <Typography color="text.secondary" fontSize={16}>
                      {t('pond:issues')}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                      {data.issues.map((problem: string) => (
                        <Chip
                          key={problem}
                          label={problem}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.error.light,
                            background: theme.palette.error.lighter,
                            color: theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ) : null}

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  width: '100%',
                  height: '100%',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: theme.palette.grey[300],
                }}
              >
                <Grid container>
                  {dashboardInfo.map((key, index, arr) => {
                    const items = arr.map((k) => info[k])
                    const item = info[key]
                    const isFull = !!item.isFull

                    const normalItems = items.filter((i) => !i.isFull)
                    const fullItemsBefore = items.slice(0, index).filter((i) => i.isFull).length
                    const effectiveIndex = index - fullItemsBefore // position among non-full items

                    const isRightColumn = !isFull && effectiveIndex % 2 === 1
                    const isLastRow =
                      isFull ||
                      effectiveIndex >= normalItems.length - (normalItems.length % 2 === 0 ? 2 : 1)

                    return (
                      <Grid
                        item
                        xs={isFull ? 12 : 6}
                        key={key}
                        sx={{
                          borderRight: (theme) =>
                            !isFull && !isRightColumn
                              ? `1px solid ${theme.palette.grey[300]}`
                              : 'none',
                          borderBottom: (theme) =>
                            !isLastRow ? `1px solid ${theme.palette.grey[300]}` : 'none',
                          borderTop: (theme) =>
                            isFull ? `1px solid ${theme.palette.grey[300]}` : 'none',
                        }}
                      >
                        <Stack sx={{ p: 2 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography color="text.secondary" fontSize={16}>
                                {item.title}
                              </Typography>
                              {item.info && !isFishSpecie && (
                                <Tooltip title={item.info} arrow>
                                  <InfoOutlinedIcon
                                    sx={{ fontSize: 16, color: 'text.disabled', cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            {item.change && (
                              <Typography color="primary.light" fontSize={14} fontWeight={600}>
                                {item.change}
                              </Typography>
                            )}
                          </Stack>
                          {isFetching && isPondDashboardFetching ? (
                            <Skeleton
                              variant="text"
                              width={120}
                              height={20}
                              sx={{ bgcolor: 'grey.300', borderRadius: 1 }}
                            />
                          ) : (
                            <Typography fontSize={18} fontWeight={600}>
                              {item.value ?? '-'}{' '}
                              {item.unit && (
                                <Typography component="span" fontWeight={600}>
                                  {item.unit}
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                    )
                  })}
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
              <ProductionChart
                title={
                  data?.status === PondStatus.Completed
                    ? info['production'].title
                    : `${t('estimate')}${info['production'].title}`
                }
                data={info['production']?.value || {}}
                unit={info['production']?.unit}
                isLoading={isFetching && isPondDashboardFetching}
                isSupport={data?.support.estimatedShrimpWeightInPond}
                hasGrowthRecord={data?.support.hasGrowthRecord}
                yieldEstimatable={data?.support.yieldEstimatable}
                status={data?.status}
                breedName={breedName?.data}
                soldCount={data?.totalSoldCount}
                inPondCount={data?.remainingCount}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} md={12} mt={3}>
            <DashboardChart />
          </Grid>
        </Card>

        <PermissionGuard featureKeys={[FeatureKey.Cost, FeatureKey.Sales]}>
          <Card sx={{ width: '100%', p: 3, mt: 3, ml: 2 }}>
            <Typography color="text.primary" fontSize={18} fontWeight={600} sx={{ mb: 2 }}>
              {t('pond:cost.title')}
            </Typography>

            <Card
              sx={{
                width: '100%',
                p: 3,
                mt: 3,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
              }}
            >
              <Typography color="text.primary" fontSize={18} fontWeight={600} sx={{ mb: 2 }}>
                {t('pond:profit.and.revenue')}
              </Typography>

              <Grid
                container
                columns={12}
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  '& > .MuiGrid-item': {
                    borderRight: { md: `1px solid ${theme.palette.grey[300]}` },
                    borderBottom: { xs: `1px solid ${theme.palette.grey[300]}`, md: 'none' },
                  },
                  '& > .MuiGrid-item:last-of-type': {
                    borderRight: 'none',
                    borderBottom: 'none',
                  },
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={4} // ใช้ 4 จาก 12 units (คือ 1/3 ของพื้นที่)
                  p={2}
                  sx={{
                    bgcolor:
                      financeData.profit < 0
                        ? 'rgba(255, 86, 48, 0.16)'
                        : 'rgba(84, 214, 44, 0.16)',
                  }}
                >
                  <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                    <Typography color="text.secondary" fontSize={16}>
                      {t('pond:total.profit')}
                      <Typography
                        component="span"
                        fontSize={12}
                        fontWeight={600}
                        color={financeData.profit > 0 ? 'success.dark' : 'error.main'}
                      >
                        {' '}
                        {financeData.profit > 0 ? '+' : ''}
                        {fNumber(financeData.profitPercentage)}%
                      </Typography>
                    </Typography>

                    <Typography color="text.primary" mt={2} fontSize={18} fontWeight={600}>
                      ฿{fNumber(financeData.profit)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4} p={2}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                    <Typography color="text.secondary" fontSize={16}>
                      {t('pond:cost')}
                    </Typography>

                    <Typography color="text.primary" mt={2} fontSize={18} fontWeight={600}>
                      ฿{fNumber(financeData.cost)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4} p={2}>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                    <Typography color="text.secondary" fontSize={16}>
                      {t('pond:total.revenue')}
                    </Typography>

                    <Typography color="text.primary" mt={2} fontSize={18} fontWeight={600}>
                      ฿{fNumber(financeData.revenue.total)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            <Grid item xs={12} md={12} mt={3}>
              <IncomeChart />
            </Grid>
          </Card>
        </PermissionGuard>
      </Grid>
    </>
  )
}

export default OverviewDashboard
